// ============================================================================
// Steadfastly - Source Badges - Detect verified regulatory citations in text
// ============================================================================
import React from 'react';

export const VERIFIED_SOURCES = [
  // US Banking
  { pattern: /31\s*CFR\s*\d+(\.\d+)?/gi, source: "CFR", verified: true, color: "emerald" },
  { pattern: /12\s*CFR\s*\d+(\.\d+)?/gi, source: "CFR", verified: true, color: "emerald" },
  { pattern: /FinCEN\s*(Advisory|Guidance|FIN-\d{4}-[A-Z]\d+)/gi, source: "FinCEN", verified: true, color: "emerald" },
  { pattern: /OCC\s*(Bulletin|Advisory|Alert)\s*\d{4}-\d+/gi, source: "OCC", verified: true, color: "emerald" },
  { pattern: /FFIEC\s*(BSA|Manual|Guidance)/gi, source: "FFIEC", verified: true, color: "emerald" },
  { pattern: /Federal\s*Reserve\s*(SR|CA)\s*\d{2,4}-\d+/gi, source: "Fed", verified: true, color: "emerald" },
  { pattern: /FDIC\s*(FIL|Advisory)\s*\d{4}-\d+/gi, source: "FDIC", verified: true, color: "emerald" },
  // UK
  { pattern: /FCA\s*(Handbook|COCON|SYSC|SUP|PRIN)/gi, source: "FCA", verified: true, color: "emerald" },
  { pattern: /PRA\s*(Rulebook|SS\d+\/\d+)/gi, source: "PRA", verified: true, color: "emerald" },
  // EU
  { pattern: /EBA\/GL\/\d{4}\/\d+/gi, source: "EBA", verified: true, color: "emerald" },
  { pattern: /AMLD\s*[456]/gi, source: "EU AML", verified: true, color: "emerald" },
  // Generic
  { pattern: /Board\s*Resolution\s*[#\d-]+/gi, source: "Internal", verified: false, color: "blue" },
  { pattern: /Policy\s*Section\s*[\d.]+/gi, source: "Internal", verified: false, color: "blue" },
];

export function detectSourceBadges(text) {
  if (!text) return [];
  const badges = [];
  const seen = new Set();
  
  for (const { pattern, source, verified, color } of VERIFIED_SOURCES) {
    const matches = text.match(pattern);
    if (matches) {
      for (const match of matches) {
        const key = `${source}-${match}`;
        if (!seen.has(key)) {
          seen.add(key);
          badges.push({ match, source, verified, color });
        }
      }
    }
  }
  return badges;
}

// Component to display text with source badges highlighted
export function TextWithSourceBadges({ text, darkMode = false, maxLength = null }) {
  if (!text) return <span className={darkMode ? "text-slate-500" : "text-slate-500"}>—</span>;
  
  const badges = detectSourceBadges(text);
  const displayText = maxLength && text.length > maxLength 
    ? text.substring(0, maxLength) + "..." 
    : text;
  
  if (badges.length === 0) {
    return <span>{displayText}</span>;
  }
  
  return (
    <div>
      <div className="mb-2">{displayText}</div>
      <div className="flex flex-wrap gap-1">
        {badges.map((badge, i) => (
          <span 
            key={i}
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
              badge.verified
                ? darkMode ? "bg-emerald-900/50 text-emerald-300 border border-emerald-700" : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                : darkMode ? "bg-blue-900/50 text-blue-300 border border-blue-700" : "bg-blue-100 text-blue-700 border border-blue-200"
            }`}
            title={badge.verified ? "Verified regulatory source" : "Internal reference"}
          >
            {badge.verified ? "✓" : "○"} {badge.source}: {badge.match}
          </span>
        ))}
      </div>
    </div>
  );
}
